import { ExerciseStateMachine } from './ExerciseStateMachine';

const createVector = (x, y, z) => ({ x, y, z });
const vectorSubtract = (v1, v2) => createVector(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
const vectorAdd = (v1, v2) => createVector(v1.x + v2.x, v1.y + v2.y, v1.z + v2.z);
const vectorScale = (v, scalar) => createVector(v.x * scalar, v.y * scalar, v.z * scalar);
const vectorLength = (v) => Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
const vectorNormalize = (v) => {
  const length = vectorLength(v);
  return length > 0 ? vectorScale(v, 1 / length) : createVector(0, 0, 0);
};
const dotProduct = (v1, v2) => v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
const crossProduct = (v1, v2) => createVector(
  v1.y * v2.z - v1.z * v2.y,
  v1.z * v2.x - v1.x * v2.z,
  v1.x * v2.y - v1.y * v2.x
);

class EMAFilter {
  constructor(alpha = 0.2) {
    this.alpha = alpha;
    this.values = {};
  }
  
  update(key, newValue) {
    if (typeof newValue !== 'number' || isNaN(newValue)) return newValue;
    
    if (this.values[key] === undefined) {
      this.values[key] = newValue;
      return newValue;
    }
    
    this.values[key] = this.alpha * newValue + (1 - this.alpha) * this.values[key];
    return this.values[key];
  }
  
  updateVector(key, vector) {
    if (!vector || typeof vector !== 'object') return vector;
    
    return {
      x: this.update(`${key}_x`, vector.x),
      y: this.update(`${key}_y`, vector.y),
      z: this.update(`${key}_z`, vector.z)
    };
  }
  
  reset() {
    this.values = {};
  }
}

class PunchEventSystem {
  constructor() {
    this.events = [];
    this.lastEventTypes = {};
    this.debounceTime = 200; // ms
  }
  
  emit(eventType, data, timestamp) {
    // Debounce events of the same type
    if (this.lastEventTypes[eventType] && 
        timestamp - this.lastEventTypes[eventType] < this.debounceTime) {
      return false;
    }
    
    this.lastEventTypes[eventType] = timestamp;
    this.events.push({ type: eventType, data, timestamp });
    return true;
  }
  
  getEvents() {
    const events = [...this.events];
    this.events = [];
    return events;
  }
  
  reset() {
    this.events = [];
    this.lastEventTypes = {};
  }
}

export class CrossBodyPunchFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: 'READY',
      
      initialStateData: {
        activeSide: 'right',
        previousWristX: 0,
        previousWristY: 0,
        previousWristZ: 0,
        previousTimestamp: 0,
        lastCrossTime: 0,
        lastReturnTime: 0,
        lastRepTime: 0,
        lastPunchFormScore: 0,
        shoulderWidth: 0,
        baselineCrossDistance: 0,  // Track baseline in READY state
        smoothedCrossDistance: 0,  // Smoothed crossDistance for stability
        maxRangeOfMotion: 0,  // Track max ROM for display
        isFirstFrame: true,
        wristPositionHistory: []
      },
      
      initialFormScores: {
        overall: 100,
        extensionScore: 100,
        velocityScore: 0,
        accuracyScore: 100,
        balance: 100,
        control: 100,
        rangeOfMotion: 0  // Initialize ROM to 0
      },
      
      states: {
        'READY': {
          description: 'Ready to detect a cross-body punch'
        },
        'CROSSED': {
          description: 'Wrist has crossed to opposite side of body'
        }
      },
      
      transitions: {
        'READY': [
          {
            targetState: 'CROSSED',
            condition: (metrics, stateData, timestamp) => {
              return metrics.hasWristCrossed === true;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastCrossTime = timestamp;
              // Reset maxRangeOfMotion for new rep
              fsm.stateData.maxRangeOfMotion = 0;
            }
          }
        ],
        'CROSSED': [
          {
            targetState: 'READY',
            condition: (metrics, stateData, timestamp) => {
              return metrics.hasWristReturned === true;
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              const score = Math.round(
                (metrics.extensionScore * 0.6) + 
                (metrics.velocityScore * 0.25) +
                (metrics.accuracyScore * 0.15)
              );
              return score;
            },
            action: (fsm, metrics, timestamp) => {
              // Note: Base class handles repHistory storage automatically
              fsm.stateData.activeSide = fsm.stateData.activeSide === 'right' ? 'left' : 'right';
              fsm.stateData.lastReturnTime = timestamp;
            }
          }
        ]
      }
    };
    
    super(config);
  }
  
  process(poseData, timestamp) {
    try {
      if (!this.validatePoseData(poseData)) {
        return { 
          isRepCompleted: false, 
          formFeedback: null, 
          exerciseState: this.getExerciseState() 
        };
      }

      const { landmarks, worldLandmarks } = this.extractLandmarks(poseData);
      
      const metrics = this.computeMetrics(landmarks, worldLandmarks, timestamp);
      
      this.updateStateData(metrics, timestamp);
      
      const transitionResult = this.checkTransitions(metrics, timestamp);
      
      this.updateFormScores(metrics, transitionResult.transitioned);
      
      this.formFeedback = this.generateFormFeedback(metrics, this.currentState);
      
      return {
        isRepCompleted: transitionResult.repCompleted,
        formFeedback: this.formFeedback,
        exerciseState: this.getExerciseState()
      };
    } catch (error) {
      return { 
        isRepCompleted: false, 
        formFeedback: "Error in processing punch",
        exerciseState: this.getExerciseState() 
      };
    }
  }
  
  validatePoseData(poseData) {
    try {
      const valid = super.validatePoseData(poseData);
      if (!valid) return false;
      
      const landmarks = poseData.landmarks[0];
      const worldLandmarks = poseData.worldLandmarks[0];
      if (!worldLandmarks || !landmarks) return false;
      
      const requiredLandmarks = [11, 12, 15, 16]; // Shoulders and wrists only
      let validLandmarksCount = 0;
      
      for (const idx of requiredLandmarks) {
        if (!worldLandmarks[idx] || typeof worldLandmarks[idx].x !== 'number') {
          continue;
        }
        
        const confidence = landmarks[idx]?.visibility || 0;
        if (confidence < 0.5) { 
          continue;
        }
        
        validLandmarksCount++;
      }
      
      const hasEnoughValidLandmarks = validLandmarksCount >= requiredLandmarks.length * 0.75;
      
      return hasEnoughValidLandmarks;
    } catch (error) {
      return false;
    }
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    try {
      const leftShoulder = worldLandmarks[11] || { x: 0, y: 0, z: 0 };
      const rightShoulder = worldLandmarks[12] || { x: 0, y: 0, z: 0 };
      const leftWrist = worldLandmarks[15] || { x: 0, y: 0, z: 0 };
      const rightWrist = worldLandmarks[16] || { x: 0, y: 0, z: 0 };
      
      const leftShoulderConf = landmarks[11]?.visibility || 0;
      const rightShoulderConf = landmarks[12]?.visibility || 0;
      const leftWristConf = landmarks[15]?.visibility || 0;
      const rightWristConf = landmarks[16]?.visibility || 0;
      
      const activeSide = this.stateData.activeSide || 'right';
      const activeWrist = activeSide === 'right' ? rightWrist : leftWrist;
      const activeShoulder = activeSide === 'right' ? rightShoulder : leftShoulder;
      const oppositeShoulder = activeSide === 'right' ? leftShoulder : rightShoulder;
      
      const activeWristConf = activeSide === 'right' ? rightWristConf : leftWristConf;
      const activeShoulderConf = activeSide === 'right' ? rightShoulderConf : leftShoulderConf;
      
      const lowConfidence = (activeWristConf < 0.6) || (activeShoulderConf < 0.6);
      
      const shoulderWidth = Math.sqrt(
        Math.pow(rightShoulder.x - leftShoulder.x, 2) + 
        Math.pow(rightShoulder.y - leftShoulder.y, 2) + 
        Math.pow(rightShoulder.z - leftShoulder.z, 2)
      );
      this.stateData.shoulderWidth = shoulderWidth;
      
      if (!this.stateData.wristPositionHistory) {
        this.stateData.wristPositionHistory = [];
      }
      
      this.stateData.wristPositionHistory.push({
        x: activeWrist.x,
        y: activeWrist.y,
        z: activeWrist.z,
        timestamp
      });
      
      if (this.stateData.wristPositionHistory.length > 10) {
        this.stateData.wristPositionHistory.shift();
      }
      
      let punchVelocity = 0;
      let hasSignificantMovement = false;

      if (this.stateData.wristPositionHistory.length >= 3) {
        const current = this.stateData.wristPositionHistory[this.stateData.wristPositionHistory.length - 1];
        const prev = this.stateData.wristPositionHistory[this.stateData.wristPositionHistory.length - 3];

        const timeDelta = (current.timestamp - prev.timestamp) / 1000;
        if (timeDelta > 0) {
          const dx = current.x - prev.x;
          const dy = current.y - prev.y;
          // NEW: Removed dz (Z-axis) for front-facing camera accuracy
          // Only use X-Y plane for punch velocity
          const distance = Math.sqrt(dx*dx + dy*dy);  // No Z component
          punchVelocity = distance / timeDelta;


          hasSignificantMovement = punchVelocity > (shoulderWidth * 1.5);
        }
      }
      
      let hasWristCrossed = false;
      if (activeSide === 'right') {
        const positionCrossed = activeWrist.x < (oppositeShoulder.x - (shoulderWidth * 0.15));
        hasWristCrossed = positionCrossed && 
                          (this.currentState === 'READY' ? 
                              (hasSignificantMovement && punchVelocity > (shoulderWidth * 1.0)) : 
                              true);
      } else {
        const positionCrossed = activeWrist.x > (oppositeShoulder.x + (shoulderWidth * 0.15));
        hasWristCrossed = positionCrossed && 
                          (this.currentState === 'READY' ? 
                              (hasSignificantMovement && punchVelocity > (shoulderWidth * 1.0)) : 
                              true);
      }
      
      let hasWristReturned = false;
      if (this.currentState === 'CROSSED') {
        if (activeSide === 'right') {
          hasWristReturned = activeWrist.x > (activeShoulder.x - (shoulderWidth * 0.05)) && 
                             hasSignificantMovement && 
                             punchVelocity > (shoulderWidth * 1.0);
        } else {
          hasWristReturned = activeWrist.x < (activeShoulder.x + (shoulderWidth * 0.05)) && 
                             hasSignificantMovement && 
                             punchVelocity > (shoulderWidth * 1.0);
        }
      }
      
      let crossDistance = 0;
      if (activeSide === 'right') {
        crossDistance = Math.max(0, oppositeShoulder.x - activeWrist.x);
      } else {
        crossDistance = Math.max(0, activeWrist.x - oppositeShoulder.x);
      }

      // Apply exponential smoothing to reduce jitter (alpha = 0.3)
      const alpha = 0.3;
      if (this.stateData.smoothedCrossDistance === 0 || this.stateData.isFirstFrame) {
        this.stateData.smoothedCrossDistance = crossDistance;
      } else {
        this.stateData.smoothedCrossDistance = 
          alpha * crossDistance + (1 - alpha) * this.stateData.smoothedCrossDistance;
      }

      // Track baseline crossDistance when in READY state (resting position)
      if (this.currentState === 'READY') {
        // Slowly update baseline to current position while at rest
        const baselineAlpha = 0.1;  // Slower update
        this.stateData.baselineCrossDistance = 
          baselineAlpha * this.stateData.smoothedCrossDistance + 
          (1 - baselineAlpha) * (this.stateData.baselineCrossDistance || 0);
      }

      const extensionScore = Math.min(100, Math.max(50, (crossDistance / shoulderWidth) * 200));

      const velocityScore = Math.min(100, Math.max(0, punchVelocity * 100));

      const heightError = Math.abs(activeShoulder.y - activeWrist.y);
      const accuracyScore = Math.min(100, Math.max(0, 100 - (heightError / shoulderWidth) * 300));

      // Calculate DISPLAY ROM (real-time feedback) based on CURRENT position (like Row)
      // NOT based on max - this way it shows 0% at rest
      let rangeOfMotion = 0;
      if (shoulderWidth > 0.01) {  // Guard against invalid shoulder width
        const baseline = this.stateData.baselineCrossDistance || 0;
        const currentMovement = this.stateData.smoothedCrossDistance - baseline;
        const targetMovement = shoulderWidth * 0.4;  // Target: move 40% of shoulder width
        rangeOfMotion = Math.min(100, Math.max(0, (currentMovement / targetMovement) * 100));
      }
      
      return {
        hasWristCrossed,
        hasWristReturned,
        extensionScore,
        velocityScore,
        accuracyScore,
        rangeOfMotion,  // ROM based on movement from baseline
        activeSide,
        lowConfidence,
        shoulderWidth,
        crossDistance: this.stateData.smoothedCrossDistance,  // Use smoothed value for stability
        punchVelocity,
        hasSignificantMovement,
        currentWristX: activeWrist.x,
        currentWristY: activeWrist.y,
        currentWristZ: activeWrist.z
      };
    } catch (error) {
      return {
        hasWristCrossed: false,
        hasWristReturned: false,
        extensionScore: 80,
        velocityScore: 80,
        accuracyScore: 80,
        activeSide: this.stateData.activeSide || 'right',
        lowConfidence: true,
        shoulderWidth: 0,
        crossDistance: 0,
        punchVelocity: 0,
        hasSignificantMovement: false,
        currentWristX: 0,
        currentWristY: 0,
        currentWristZ: 0
      };
    }
  }
  
  updateStateData(metrics, timestamp) {
    try {
      this.stateData = {
        ...this.stateData,
        previousWristX: metrics.currentWristX,
        previousWristY: metrics.currentWristY,
        previousWristZ: metrics.currentWristZ,
        previousTimestamp: timestamp,
        isFirstFrame: false,
        shoulderWidth: metrics.shoulderWidth || this.stateData.shoulderWidth
      };
      
      // Track maximum ROM achieved during this rep ONLY while in CROSSED state (BEFORE checkTransitions)
      if (this.currentState === 'CROSSED') {
        this.stateData.maxRangeOfMotion = Math.max(
          this.stateData.maxRangeOfMotion || 0,
          metrics.rangeOfMotion || 0
        );
      }
    } catch (error) {
      // Silent error handling
    }
  }
  
  updateFormScores(metrics, transitioned) {
    try {
      const formScores = {
        ...this.formScores,
        extensionScore: metrics.extensionScore,
        velocityScore: metrics.velocityScore,
        accuracyScore: metrics.accuracyScore
      };

      // ROM: Always show CURRENT/REAL-TIME ROM for live feedback
      if (metrics.rangeOfMotion !== undefined) {
        formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion);
      }

      if (this.stateData.repFormScore) {
        formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
        this.stateData.repFormScore = 0;
      } else {
        const currentOverall = Math.round(
          (metrics.extensionScore * 0.6) +
          (metrics.velocityScore * 0.25) +
          (metrics.accuracyScore * 0.15)
        );
        formScores.overall = Math.round(this.formScores.overall * 0.7 + currentOverall * 0.3);
      }

      this.formScores = formScores;
    } catch (error) {
      // Silent error handling
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    try {
      // Disable FSM form feedback to prevent duplicate messages
      // The milestone and coaching system handles all feedback
      return null;
    } catch (error) {
      return null;
    }
  }
  
  checkTransitions(metrics, timestamp) {
    try {
      const availableTransitions = this.transitions[this.currentState] || [];
      
      const minTimeBetweenReps = 1000; 
      const isRepDebounced = (this.stateData.lastRepTime && 
                             (timestamp - this.stateData.lastRepTime < minTimeBetweenReps));
      
      for (const transition of availableTransitions) {
        try {
          const conditionMet = transition.condition(metrics, this.stateData, timestamp);
          if (conditionMet) {
            if (transition.action) {
              transition.action(this, metrics, timestamp);
            }
            
            this.lastTransitionTime = timestamp;
            
            let repCompleted = false;
            if (transition.completesRepetition && !isRepDebounced) {
              this.repetitionCount++;
              repCompleted = true;
              this.stateData.lastRepTime = timestamp;
              
              if (transition.calculateRepScore) {
                this.stateData.repFormScore = transition.calculateRepScore(metrics, this.stateData);
              }
              
              // Store per-rep data for session analytics (base class doesn't run since we override)
              const repROM = Math.round(this.stateData.maxRangeOfMotion || 0);
              this.repHistory.push({
                rep: this.repetitionCount,
                formScore: this.stateData.repFormScore || 0,
                rom: repROM,
                timestamp: timestamp
              });
              
              // Reset maxRangeOfMotion for next rep
              this.stateData.maxRangeOfMotion = 0;
            }
            
            this.currentState = transition.targetState;
            
            return { transitioned: true, repCompleted };
          }
        } catch (conditionError) {
          // Silent error handling
        }
      }
        
      const maxTimeInCrossed = 3000; 
      if (this.currentState === 'CROSSED' && 
          timestamp - this.stateData.lastCrossTime > maxTimeInCrossed && 
          !isRepDebounced) {
        
        let countAsRep = metrics.hasSignificantMovement;
        
        const score = Math.round(
          (metrics.extensionScore * 0.6) + 
          (metrics.velocityScore * 0.25) +
          (metrics.accuracyScore * 0.15)
        );
        
        this.stateData.repFormScore = score;
        
        if (countAsRep) {
          // Note: Base class handles repHistory and repetition count automatically
          // But for timeout case, we need to handle it manually
          this.repetitionCount++;
          
          // Store per-rep data using maxRangeOfMotion
          const repROM = Math.round(this.stateData.maxRangeOfMotion || 0);
          this.repHistory.push({
            rep: this.repetitionCount,
            formScore: score,
            rom: repROM,
            timestamp: timestamp
          });
        }
        
        this.stateData.activeSide = this.stateData.activeSide === 'right' ? 'left' : 'right';
        
        this.currentState = 'READY';
        this.lastTransitionTime = timestamp;
        this.stateData.lastRepTime = timestamp;
        
        this.stateData.lastCrossTime = timestamp;
        
        // Reset maxRangeOfMotion for next rep
        this.stateData.maxRangeOfMotion = 0;
        
        return { transitioned: true, repCompleted: countAsRep };
      }
      
      return { transitioned: false, repCompleted: false };
    } catch (error) {
      return { transitioned: false, repCompleted: false };
    }
  }
  
  setActiveSide(side) {
    if (side === 'left' || side === 'right') {
      this.stateData.activeSide = side;
    }
  }
  
  reset() {
    // Call parent reset method which handles the proper initialization
    super.reset();
    
    // Reset specific Cross Body Punch FSM data
    this.stateData.isFirstFrame = true;
    this.stateData.wristPositionHistory = [];
  }
} 